import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { GREENING_BRAND } from '../greeningFabActions';
import { GreeningVoiceOverlay } from './GreeningVoiceOverlay';
import { GreeningVoiceVisualizer, VoiceVisualState } from './GreeningVoiceVisualizer';
import { SpeechSegment } from './browserSpeech';
import { GreeningVoicePicker } from './GreeningVoicePicker';
import { GreeningVoiceControls } from './GreeningVoiceControls';
import { useGreeningTts } from './useGreeningTts';
import { ensureStartVoiceProfileId, PADRAO3_BROWSER } from './voiceProfiles';
import { activateVoiceSurface, bindVoiceClose, bindVoiceStop, voiceSurfaceOwner } from './voiceSurface';

interface Props {
  open: boolean;
  segments: SpeechSegment[];
  subject?: string;
  onReading?: (reading: { section: string; ratio: number } | null) => void;
  onClose: () => void;
}

function introLine(subject?: string): string {
  const topic = (subject || '').trim();
  if (!topic) return 'Olá, eu sou a GreeningAI e serei o seu assistente jurídico.';
  return `Olá, eu sou a GreeningAI e serei o seu assistente jurídico sobre ${topic}.`;
}

/** Escuta da análise já pronta. A cápsula é a da conversa, sem microfone e sem novo pedido. */
export function GreeningListenPanel({ open, segments, subject, onReading, onClose }: Props) {
  const [state, setState] = useState<VoiceVisualState>('idle');
  const [held, setHeld] = useState<'listening' | 'speaking' | null>(null);
  const endedRef = useRef(false);
  const { speak, stop, pause, resume, getCursor, activeDisplayName, fallbackNotice, progress } = useGreeningTts(() => {
    endedRef.current = true;
    setHeld(null);
    setState('idle');
    onReading?.(null);
  });

  const script = JSON.stringify(segments);
  const scriptRef = useRef(script);
  const subjectRef = useRef(subject);
  const speakRef = useRef(speak);
  const stopRef = useRef(stop);
  const onReadingRef = useRef(onReading);
  const onCloseRef = useRef(onClose);
  scriptRef.current = script;
  subjectRef.current = subject;
  speakRef.current = speak;
  stopRef.current = stop;
  onReadingRef.current = onReading;
  onCloseRef.current = onClose;

  useEffect(() => bindVoiceClose('listen', () => onCloseRef.current()), []);
  useEffect(() => bindVoiceStop('listen', () => stopRef.current()), []);

  useEffect(() => {
    if (!open) return undefined;
    const queue = JSON.parse(scriptRef.current) as SpeechSegment[];
    if (queue.length) {
      endedRef.current = false;
      ensureStartVoiceProfileId();
      setHeld(null);
      setState('speaking');
      activateVoiceSurface('listen');
      const section = queue.find((segment) => segment.section)?.section;
      if (section) onReadingRef.current?.({ section, ratio: 0 });
      speakRef.current(queue, { intro: introLine(subjectRef.current) });
    }
    return () => {
      if (voiceSurfaceOwner() === 'listen') stopRef.current();
    };
  }, [open]);

  useEffect(() => {
    if (endedRef.current || !progress?.section) return;
    onReading?.({ section: progress.section, ratio: progress.ratio });
  }, [onReading, progress?.section, progress?.ratio]);

  const finish = () => {
    stop();
    onClose();
  };

  const onToggleHold = () => {
    if (state === 'paused') {
      setHeld(null);
      resume();
      setState('speaking');
      return;
    }
    if (state === 'speaking') {
      pause();
      setHeld('speaking');
      setState('paused');
    }
  };

  return (
    <GreeningVoiceOverlay
      open={open}
      framed
      title="Ouvir análise"
      subtitle="Escolha a voz da Greening.AI e acompanhe a leitura."
      onClose={finish}
    >
      <GreeningVoicePicker
        compact
        vivid
        excludeIds={[PADRAO3_BROWSER.id]}
        onChange={(profileId) => {
          if (state !== 'speaking' && state !== 'paused') return;
          endedRef.current = false;
          const queue = JSON.parse(script) as SpeechSegment[];
          if (!queue.length) return;
          const cursor = getCursor();
          if (cursor.section) onReading?.({ section: cursor.section, ratio: cursor.ratio });
          setHeld(null);
          setState('speaking');
          speak(queue, { profileId, resume: cursor });
        }}
      />
      {fallbackNotice ? (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', textAlign: 'center', mt: 0.5, mb: 0.5 }}>
          {fallbackNotice}
        </Typography>
      ) : null}
      <GreeningVoiceVisualizer state={state} analyser={null} />
      {state === 'speaking' && activeDisplayName ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
          <Typography
            variant="caption"
            align="center"
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: '16px',
              color: '#fff',
              backgroundImage: `linear-gradient(135deg, ${GREENING_BRAND.green} 0%, ${GREENING_BRAND.teal} 100%)`,
            }}
          >
            Lendo com {activeDisplayName}
          </Typography>
        </Box>
      ) : null}
      <GreeningVoiceControls
        listenOnly
        state={state}
        held={held}
        onListen={() => undefined}
        onStopListen={() => undefined}
        onToggleHold={onToggleHold}
      />
    </GreeningVoiceOverlay>
  );
}
